import React from 'react';
import { asText } from '@lib/prismic/util/document';
import env from '@lib/envs/env';
import { Link } from 'prismic-reactjs';
import {
  IPrismicImage, IPrismicMetaKeyword, IPrismicText, IPrismicUrl
} from '@lib/prismic/types';
import Compose from '@components/shared/Compose';
import ThemeProvider from '@components/theme/ThemeProvider';
import FullBleedHero from '@components/shared/Hero/FullBleedHero/FullBleedHero';

import dynamic from 'next/dynamic';
import parseUrl from '@lib/prismic/util/parseUrl';
import styles from './InteriorDesignServices.module.scss';
import { IStep } from './Steps/IStep';

const DefaultTemplate = dynamic(() => import('@components/shared/templates/DefaultTemplate/DefaultTemplate'));
const Steps = dynamic(() => import('./Steps/Steps'));
const GetStarted = dynamic(() => import('./GetStarted/GetStarted'));

interface IPrismicData {
  meta_title: string;
  meta_description: string;
  meta_keywords: IPrismicMetaKeyword[];
  hero_title: IPrismicText[];
  hero_cta_link: IPrismicUrl;
  hero_cta_text: IPrismicText[];
  main_hero_image: IPrismicImage;
  description_title: IPrismicText[];
  description_subtitle: IPrismicText[];
  description_cta_text: IPrismicText[];
  description_cta_link: IPrismicUrl;
  description_cta_logged_in_link: IPrismicUrl;
  steps: IStep[];
  get_started_image: IPrismicImage;
  get_started_eyebrow: IPrismicText[];
  get_started_title: IPrismicText[];
  get_started_description: IPrismicText[];
  get_started_cta_text: IPrismicText[];
  get_started_cta_link: IPrismicUrl;
  get_started_cta_logged_in_link: IPrismicUrl;
}
interface IInteriorDesignServicesProps {
  prismicData: IPrismicData;
}

export default function InteriorDesignServices({
  prismicData
}: IInteriorDesignServicesProps) {
  const {
    meta_title,
    meta_description,
    meta_keywords,
    main_hero_image,
    description_title,
    description_subtitle,
    description_cta_text,
    description_cta_link,
    description_cta_logged_in_link,
    steps,
    get_started_image,
    get_started_eyebrow,
    get_started_title,
    get_started_description,
    get_started_cta_text,
    get_started_cta_link,
    get_started_cta_logged_in_link,
  } = prismicData;

  const animImages = [
    'https://assets4.lottiefiles.com/private_files/lf30_r1zedxdg.json',
    'https://assets1.lottiefiles.com/packages/lf20_jhvaqzfc.json',
    'https://assets3.lottiefiles.com/packages/lf20_mncarwxc/data.json',
    'https://assets1.lottiefiles.com/packages/lf20_ctlooiaa.json',
    'https://assets5.lottiefiles.com/packages/lf20_slcyk9qd.json'
  ];

  return (
    <Compose
      components={[
        ThemeProvider
      ]}
    >
      <DefaultTemplate
        canonicalLink={`https://${env.DOMAIN_NAME}/interior-design-services`}
        data-test="interior-design-services-page"
        title={meta_title}
        description={meta_description}
        keywords={meta_keywords?.map(
          (metaKeyword: IPrismicMetaKeyword) => metaKeyword.meta_keyword
        )}
        navProps={{ isTransparent: true, isFixed: true }}
      >

        <FullBleedHero
          hero_title={description_title}
          hero_subtitle={description_subtitle}
          hero_cta_text={description_cta_text}
          hero_cta_link={description_cta_link}
          hero_cta_logged_in_link={description_cta_logged_in_link}
          main_hero_image={main_hero_image}
        />
        <div className={styles.Headline}>
          <p>Design it. Shop it. Bring it to life.</p>
        </div>
        <Steps
          className={styles.StepsWrapper}
          steps={steps.map((step: any, index: number): IStep => ({
            description: asText(step.step_description),
            eyebrow: asText(step.step_eyebrow),
            animatedImage: animImages[index]
          }))}
        />
        <GetStarted
          className={''}
          image={get_started_image}
          eyebrow={asText(get_started_eyebrow)}
          title={asText(get_started_title)}
          description={asText(get_started_description)}
          ctaText={asText(get_started_cta_text)}
          ctaLink={parseUrl(Link.url(get_started_cta_link))}
          ctaLoggedInLink={parseUrl(Link.url(get_started_cta_logged_in_link))}
        />
      </DefaultTemplate>
    </Compose>
  );
}
